import * as d3 from 'd3';

import PlantNetworkFilter from './PlantNetworkFilter';
import React from 'react';

type D3TestPropsType = {
    width: number;
    height: number;
    data: ChartType;
    history: any;
    botanicalId: string;
};

class D3Test extends React.Component<
    D3TestPropsType,
    { visualizationBox: any; filters: Record<string, any>; filteredData: ChartType; filterTouched: boolean }
> {
    ref: SVGSVGElement | undefined;

    constructor(props: any) {
        super(props);
        this.state = {
            visualizationBox: undefined, // {x: number; y: number; height: number; width: number }
            filters: {},
            filteredData: { key: '', name: '' },
            filterTouched: false,
        };
        this.drawChart = this.drawChart.bind(this);
    }

    componentDidMount(): void {
        // this.drawChart();
    }
    componentDidUpdate(): void {
        // clear canvas if any
        // d3.select(this.ref).remove(); // remove the whole ref from DOM
        d3.selectAll('svg.container > *').remove();
        this.prepareFilters();
        this.drawChart();
    }

    // prepare list of filter names from data categories
    // if the category doesn't have children ignore it
    prepareFilters(): void {
        const { data } = this.props;
        const filters: any = {};

        if (data.children) {
            data.children.map((item) => {
                if (item.children && item.children?.length > 0) {
                    filters[item.name] = true;
                }
            });
        }
        if (Object.keys(this.state.filters).join() !== Object.keys(filters).join()) {
            this.setState({ filters: filters });
        }
    }

    // create a children list from data where the children are filtered out
    // if aren't present in the selected filters
    filterData(selectedFilters: string[]): void {
        this.setState({ filterTouched: true });

        const filteredData: ChartType = {
            key: this.props.data.key,
            name: this.props.data.name,
            children: [],
        };

        //filter out data setted in filters
        filteredData.children = this.props.data.children?.filter((item) => {
            return selectedFilters.indexOf(item.name) > -1;
        });
        this.setState({ filteredData: filteredData });
    }

    drawChart(): void {
        const { width, height } = this.props;
        let data = this.state.filteredData;
        if (!this.state.filterTouched) {
            data = this.props.data;
        }

        // put data in the cluster layout
        const root = d3.hierarchy(data).sort((a: any, b: any) => d3.ascending(a.data.name, b.data.name));
        root.dx = 40;
        root.dy = width / (root.height + 1);
        // create cluster layout
        const tree = d3.cluster().nodeSize([root.dx, root.dy]);
        tree(root);

        const { history } = this.props;
        const c = {
            red: '#FF006E',
            purple: '#8338EC',
            blue: '#3A86FF',
            orange: '#FB5607',
            yellow: '#FFBE0B',
        };

        if (this.ref !== undefined) {
            // add curves between nodes
            d3.select(this.ref)
                .append('g')
                .attr('fill', 'none')
                .attr('stroke', '#555')
                .attr('stroke-opacity', 0.4)
                .attr('stroke-width', 1.5)
                .selectAll('path')
                .data(root.links())
                .join('path')
                .attr(
                    'd',
                    (d: any) => `
                    M${d.target.y},${d.target.x}
                    C${d.source.y + root.dy / 2},${d.target.x}
                     ${d.source.y + root.dy / 2},${d.source.x}
                     ${d.source.y},${d.source.x}
                  `,
                );

            // Add a circle for each node.
            d3.select(this.ref)
                .append('g')
                .selectAll('circle')
                .data(root.descendants())
                .join('circle')
                .attr('cx', (d: any) => d.y)
                .attr('cy', (d: any) => d.x)
                .attr('fill', (d: any) => (d.children ? '#555' : '#999'))
                .attr('r', 2.5);

            // add text labels
            d3.select(this.ref)
                .append('g')
                .attr('font-family', 'sans-serif')
                .attr('font-size', 10)
                .attr('stroke-linejoin', 'round')
                .attr('stroke-width', 3)
                .selectAll('text')
                .data(root.descendants())
                .join('text')
                .attr('x', (d: any) => d.y)
                .attr('y', (d: any) => d.x)
                .attr('dy', '0.31em')
                .attr('dx', (d: any) => (d.children ? -6 : 6))
                .text((d: any) => d.data.name)
                .style('fill', (d: any) =>
                    d.depth === 0
                        ? c.red
                        : d.depth === 1
                        ? d.data.name === 'pinyin'
                            ? c.blue
                            : d.data.name === 'synonyms'
                            ? c.blue
                            : d.data.name === 'common_name'
                            ? c.blue
                            : d.data.name === 'chinese_pharmaceutical_name'
                            ? c.blue
                            : d.data.name === 'pharmaceutical_latin_name'
                            ? c.blue
                            : 'black'
                        : d.depth === 2
                        ? d.parent.data.name === 'pinyin'
                            ? c.red
                            : d.parent.data.name === 'synonyms'
                            ? c.blue
                            : d.parent.data.name === 'common_name'
                            ? c.blue
                            : d.parent.data.name === 'chinese_pharmaceutical_name'
                            ? c.red
                            : d.parent.data.name === 'pharmaceutical_latin_name'
                            ? c.red
                            : 'black'
                        : 'black',
                )
                .style('font-size', (d: any) => (d.depth === 0 ? '30px' : '20px'))
                .style('cursor', (d: any) =>
                    d.depth === 0
                        ? 'pointer'
                        : d.depth === 2
                        ? d.parent.data.name === 'pinyin' ||
                          d.parent.data.name === 'chinese_pharmaceutical_name' ||
                          d.parent.data.name === 'pharmaceutical_latin_name'
                            ? 'pointer'
                            : ''
                        : '',
                )
                .style('text-decoration', (d: any) =>
                    d.depth === 0
                        ? 'underline'
                        : d.depth === 2
                        ? d.parent.data.name === 'pinyin' ||
                          d.parent.data.name === 'chinese_pharmaceutical_name' ||
                          d.parent.data.name === 'pharmaceutical_latin_name'
                            ? 'underline'
                            : ''
                        : '',
                )
                .style('font-weight', (d: any) =>
                    d.depth === 0
                        ? 'bold'
                        : d.depth === 2
                        ? d.parent.data.name === 'pinyin' ||
                          d.parent.data.name === 'chinese_pharmaceutical_name' ||
                          d.parent.data.name === 'pharmaceutical_latin_name'
                            ? 'bold'
                            : ''
                        : '',
                )
                .on('click', (d: any) => {
                    if (d.depth === 0) {
                        history.push('/plant/detail/' + this.props.botanicalId);
                    } else if (d.depth === 2) {
                        if (
                            d.parent.data.name === 'chinese_pharmaceutical_name' ||
                            d.parent.data.name === 'pinyin' ||
                            d.parent.data.name === 'pharmaceutical_latin_name'
                        ) {
                            history.push('/plant/herb/' + d.data.chinese_id);
                        }
                    }
                })
                .filter((d: any) => d.children)
                .attr('text-anchor', 'end')
                .clone(true)
                .lower()
                .attr('stroke', 'white');

            // get bounding box size and position
            const box = this.ref.getBBox();
            if (!this.state.visualizationBox) this.setState({ visualizationBox: box });
            d3.select(this.ref).attr('viewBox', () => [box.x, box.y, box.width, box.height]);
        }
    }

    render(): JSX.Element {
        return (
            <div>
                <PlantNetworkFilter
                    filters={Object.keys(this.state.filters)}
                    callback={(selectedFilters): void => {
                        this.filterData(selectedFilters);
                    }}
                    style={{ marginBottom: '40px' }}
                />
                <svg
                    className="container"
                    ref={(ref: SVGSVGElement): SVGSVGElement => (this.ref = ref)}
                    width="100%"
                    height="100%"
                ></svg>
            </div>
        );
    }
}

export default D3Test;
